/**
 * What a Worker is doing, under its terminal.
 *
 * Read-only, and deliberately only what the platform can actually say. A summary that guesses
 * is worse than one that is short: this reads the Worker's own row, the terminal's state and
 * the record of what has been done to that terminal, and names the things nothing records yet
 * rather than inventing them from the output.
 *
 * The terminal above remains the source of truth. This is the part that can be glanced at.
 */
import { useEffect, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { Reader, TerminalActionRow, WorkerTerminalRow } from '../lib/data';
import { actionLine, attentionFor } from '../lib/worker-terminal-view';
import { terminalProcessText, terminalTransportText, type TerminalTransportState } from '../lib/terminal-attachment';
import '../worker-summary.css';

const REFRESH_MS = 4000;

/** What Brunel cannot yet say about a Worker, named rather than guessed. */
const NOT_RECORDED_YET = [
  'tests run and their result',
  'files changed',
  'what it intends to do next',
] as const;

export interface WorkerSummaryProps {
  readonly client: SupabaseClient;
  readonly orgId: string;
  readonly reader: Reader;
  readonly row: WorkerTerminalRow & { session_id: string };
  readonly transport: TerminalTransportState;
}

export function WorkerSummary({ orgId, reader, row, transport }: WorkerSummaryProps): ReactElement {
  const [actions, setActions] = useState<{ sessionId: string; rows: readonly TerminalActionRow[] } | null>(null);
  const [activityOpen, setActivityOpen] = useState(false);
  const currentActions = actions?.sessionId === row.session_id ? actions.rows : null;

  useEffect(() => {
    if (!activityOpen) return undefined;
    let live = true;
    let inFlight = false;
    let timer: number | undefined;
    const read = async () => {
      if (!live || document.hidden || inFlight) return;
      inFlight = true;
      try {
        const result = await reader.terminalActions(orgId, row.session_id, 8);
        if (live && !document.hidden && result.ok) setActions({ sessionId: row.session_id, rows: result.rows });
      } finally {
        inFlight = false;
      }
    };
    const onVisibility = () => {
      if (timer !== undefined) window.clearInterval(timer);
      if (document.hidden) return;
      void read();
      timer = window.setInterval(() => void read(), REFRESH_MS);
    };
    document.addEventListener('visibilitychange', onVisibility);
    onVisibility();
    return () => {
      live = false;
      document.removeEventListener('visibilitychange', onVisibility);
      if (timer !== undefined) window.clearInterval(timer);
    };
  }, [reader, orgId, row.session_id, activityOpen]);

  const attention = attentionFor(row);

  return (
    <section className={`worker-summary stack tone-${attention.tone}`} aria-label={`Worker ${row.worker_number} status`}>
      <p className="row spread wrap">
        <strong>{attention.line}</strong>
        <small className="muted">Transport · {terminalTransportText(true, transport)}</small>
      </p>

      <dl className="worker-facts">
        <div><dt>Task</dt><dd>{row.task_title ?? 'None running'}</dd></div>
        <div><dt>Project</dt><dd>{row.project_name ?? 'Not assigned'}</dd></div>
        <div><dt>Computer</dt><dd>{row.node_name ?? 'None'}</dd></div>
        <div><dt>AI</dt><dd>{row.offering_key ?? 'None chosen'}</dd></div>
        <div><dt>Worker</dt><dd>{row.worker_status}</dd></div>
        <div><dt>PTY</dt><dd>{terminalProcessText(true, row.terminal_status, row.closed_at)}{row.pid === null ? '' : ` · process ${row.pid}`}</dd></div>
        <div><dt>Folder</dt><dd>{row.cwd ?? 'the computer’s own'}</dd></div>
        <div><dt>Keyboard</dt><dd>{row.input_held && row.input_holder !== null ? row.input_holder.replaceAll('_', ' ') : 'free'}</dd></div>
      </dl>

      <details onToggle={(event) => setActivityOpen(event.currentTarget.open)}>
        <summary>Recent activity{activityOpen && currentActions !== null ? ` (${currentActions.length} last read)` : ''}</summary>
        {currentActions === null
          ? <p className="muted" role="status">Recent activity is Unknown until Brunel reads it.</p>
          : currentActions.length === 0
          ? <p className="muted">Nothing has been sent to this terminal yet.</p>
          : (
            <ul className="worker-activity">
              {currentActions.map((action) => (
                <li key={action.id}>
                  <span>{actionLine(action)}</span>{' '}
                  <small className="muted">{new Date(action.at).toLocaleTimeString()}</small>
                </li>
              ))}
            </ul>
          )}
        <small className="muted">
          What was typed is never recorded - only that it was, how much, and what was decided.
        </small>
      </details>

      <details>
        <summary>Not recorded yet</summary>
        <ul className="worker-activity">
          {NOT_RECORDED_YET.map((item) => <li key={item} className="muted">{item}</li>)}
        </ul>
        <small className="muted">
          These need events from the tool itself, not a reading of what it printed. The terminal is the source of truth until then.
        </small>
      </details>
    </section>
  );
}
